/* VanillaGoods Dessert Builder: the rules and the composing (vgb-core.js).
   Pure functions over the data file (vgb-data.json) and the recipe details the page fetches
   (/blogs/recipes/<handle>?view=vgb), so tests/vgb/run.js runs them in Node with no DOM.
   Source of truth: vanilla_recipe_generator_v2/shopify/dessert_builder. ASCII only (theme upload). */
(function (root) {
  'use strict';
  var VGB = root.VGB = root.VGB || {};
  var C = VGB.core = {};

  /* ------------------------------------------------------------------ data --- */

  /** Index the data file: components by handle, types by key, pairs as a set. */
  C.index = function (data) {
    var byHandle = {}, types = {}, pairs = {};
    (data.components || []).forEach(function (c) { byHandle[c.h] = c; });
    (data.types || []).forEach(function (t) { types[t.key] = t; });
    (data.pairs || []).forEach(function (p) { pairs[p[0] + '|' + p[1]] = true; pairs[p[1] + '|' + p[0]] = true; });
    return { data: data, byHandle: byHandle, types: types, pairs: pairs };
  };

  C.slot = function (type, key) {
    for (var i = 0; i < type.slots.length; i++) if (type.slots[i].key === key) return type.slots[i];
    return null;
  };

  /* ------------------------------------------------------------------ rules --- */

  function has(list, v) { return (list || []).indexOf(v) !== -1; }

  /** Can this component take this slot given what is already chosen? {ok, why, note} */
  C.allowed = function (idx, type, slot, comp, state) {
    if (!comp || comp.r !== slot.role) return { ok: false, why: 'not a ' + slot.role };
    if (!has(comp.c, type.key)) return { ok: false, why: 'not written for ' + type.noun };
    var base = state.base ? idx.byHandle[state.base] : null;
    var filling = state.filling ? idx.byHandle[state.filling] : null;
    var r = type.rules || {};
    var note = '';
    if (type.key === 'cake' || type.key === 'cupcake') {
      if (slot.key === 'filling') {
        if (comp.p === 'baked-in') return { ok: false, why: 'bakes inside a crust, not between layers' };
        if (comp.s === 'loose') return { ok: false, why: 'too loose to stay between layers' };
        if (comp.s === 'soft') note = type.key === 'cake' ? 'Pipe a ring of frosting around the edge first so it stays put.' : 'Chill it well before piping into the centers.';
      }
      if (slot.key === 'frosting' && comp.x === 'glaze' && type.key === 'cake') note = 'A glaze gives a thin, poured finish rather than a frosted one.';
    }
    if (type.key === 'pie') {
      if (slot.key === 'filling' && base) {
        if (comp.p === 'baked-in' && !has(r.baked_in_needs_crust, base.k)) return { ok: false, why: 'bakes with the pie: needs an unbaked crust' };
        if (comp.p !== 'baked-in' && !has(r.chilled_needs_crust, base.k)) return { ok: false, why: 'a chilled filling needs a baked or crumb crust' };
        if (base.k === 'double' && comp.x === 'custard') return { ok: false, why: 'custard pies do not take a top crust' };
      }
      if (slot.key === 'crunch') {
        if (base && base.k === 'double') return { ok: false, why: 'the top crust is the top' };
        if (filling && filling.p === 'baked-in' && comp.x !== 'fruit') return { ok: false, why: 'a crunchy finish goes on a cream or custard pie' };
        if (filling && filling.p === 'baked-in') note = 'Add the fruit once the pie is fully cooled.';
      }
      if (slot.key === 'topping') {
        if (base && base.k === 'double') return { ok: false, why: 'the top crust is the top' };
        if (filling) {
          if (comp.x === 'crumble' && filling.p !== 'baked-in') return { ok: false, why: 'streusel bakes on a fruit or custard filling' };
          if (comp.x === 'whipped' && filling.p === 'baked-in' && filling.x === 'fruit') note = 'Add it once the pie is fully cooled.';
        }
      }
    }
    if (type.key === 'cheesecake') {
      if (slot.key === 'topping' && comp.x === 'crumble') note = 'Bake the streusel on a sheet pan and scatter it over the chilled cheesecake.';
    }
    if (type.key === 'doughnut') {
      if (slot.key === 'filling' && base) {
        if (!has(r.filling_needs_dough, base.k)) return { ok: false, why: 'cake and baked doughnuts cannot be filled' };
        if (base.k === 'yeast') note = 'Cut rounds instead of rings so there is room for the filling.';
      }
      if (slot.key === 'frosting' && base && base.k === 'cake' && comp.x === 'ganache') note = 'Dip the craggy top only; a thick frosting hides the crunch.';
    }
    return { ok: true, note: note };
  };

  /** How many chosen components this one pairs well with (same family counts). */
  C.pairScore = function (idx, comp, state, skipSlot) {
    var n = 0;
    if (!comp.fam) return 0;
    Object.keys(state).forEach(function (k) {
      if (k === skipSlot || k === 'type' || k === 'servings') return;
      var o = idx.byHandle[state[k]];
      if (!o || !o.fam) return;
      if (o.fam === comp.fam || idx.pairs[o.fam + '|' + comp.fam]) n++;
    });
    return n;
  };

  /** The options for one slot: every component of the slot's role that fits the type, allowed or not, ranked. */
  C.options = function (idx, type, slotKey, state, query) {
    var slot = C.slot(type, slotKey);
    if (!slot) return [];
    var q = (query || '').toLowerCase().trim();
    var out = [];
    // "pairs well" means it pairs with every flavored part already chosen, not just one of them
    var chosen = 0;
    Object.keys(state).forEach(function (k) { if (k !== slotKey && k !== 'type' && k !== 'servings' && idx.byHandle[state[k]] && idx.byHandle[state[k]].fam) chosen++; });
    idx.data.components.forEach(function (c) {
      if (c.r !== slot.role || !has(c.c, type.key)) return;
      if (q && (c.t + ' ' + c.f + ' ' + c.x + ' ' + (c.d || []).join(' ')).toLowerCase().indexOf(q) === -1) return;
      var a = C.allowed(idx, type, slot, c, state);
      var p = a.ok ? C.pairScore(idx, c, state, slotKey) : 0;
      out.push({ comp: c, ok: a.ok, why: a.why || '', note: a.note || '', pairs: p, all: chosen > 0 && p >= chosen });
    });
    out.sort(function (a, b) {
      if (a.ok !== b.ok) return a.ok ? -1 : 1;
      if (a.pairs !== b.pairs) return b.pairs - a.pairs;
      return a.comp.t < b.comp.t ? -1 : 1;
    });
    return out;
  };

  /** Which required slots are still empty, and which chosen components no longer fit (after a base change). */
  C.check = function (idx, type, state) {
    var missing = [], conflicts = [];
    type.slots.forEach(function (s) {
      var h = state[s.key];
      if (!h) { if (s.required) missing.push(s.key); return; }
      var c = idx.byHandle[h];
      var a = c ? C.allowed(idx, type, s, c, state) : { ok: false, why: 'unknown recipe' };
      if (!a.ok) conflicts.push({ slot: s.key, handle: h, why: a.why });
    });
    return { missing: missing, conflicts: conflicts, complete: !missing.length && !conflicts.length };
  };

  /* ------------------------------------------------------------------ quantities --- */

  var GLYPH = { '\u00bd': 0.5, '\u2153': 1 / 3, '\u2154': 2 / 3, '\u00bc': 0.25, '\u00be': 0.75, '\u215b': 0.125, '\u215c': 0.375, '\u215d': 0.625, '\u215e': 0.875 };

  /** "1 1/2", "3/4", "1.5", "\u00bd" -> number; NaN when empty. */
  C.num = function (s) {
    if (typeof s === 'number') return s;
    s = String(s == null ? '' : s).trim();
    if (!s) return NaN;
    var m, total = 0, parts = s.split(/\s+/), ok = false;
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i];
      if (GLYPH[p] != null) { total += GLYPH[p]; ok = true; continue; }
      if ((m = p.match(/^(\d+)([\u00bc-\u00be\u2153-\u215e])$/))) { total += +m[1] + GLYPH[m[2]]; ok = true; continue; }
      if ((m = p.match(/^(\d+)\/(\d+)$/))) { if (+m[2]) { total += m[1] / m[2]; ok = true; } continue; }
      if (/^\d+(\.\d+)?$/.test(p)) { total += parseFloat(p); ok = true; continue; }
      return NaN;
    }
    return ok ? total : NaN;
  };

  var FRACS = [[0, ''], [0.125, '1/8'], [0.25, '1/4'], [0.333, '1/3'], [0.375, '3/8'], [0.5, '1/2'], [0.625, '5/8'], [0.667, '2/3'], [0.75, '3/4'], [0.875, '7/8'], [1, '']];

  /** A kitchen-friendly amount: 1.5 -> "1 1/2", 0.333 -> "1/3", 2.9 -> "3", 12.4 -> "12 1/2" (spoons and cups stay in eighths). */
  C.fmt = function (n, unit) {
    if (!isFinite(n) || n <= 0) return '';
    if (/^(g|ml)$/.test(unit || '')) return String(n < 10 ? Math.round(n * 2) / 2 : Math.round(n)).replace(/\.0$/, '');
    if (unit === 'oz' || n >= 20) return String(n < 100 ? Math.round(n * 2) / 2 : Math.round(n)).replace(/\.0$/, '');
    var whole = Math.floor(n), rest = n - whole, best = FRACS[0], d = 1;
    // quarters and thirds when one is close (as a recipe would print it), eighths otherwise (a 1/8 teaspoon is real)
    var coarse = FRACS.filter(function (f) { return f[0] * 4 === Math.round(f[0] * 4) || f[1] === '1/3' || f[1] === '2/3'; });
    var i, dd;
    for (i = 0; i < coarse.length; i++) { dd = Math.abs(coarse[i][0] - rest); if (dd < d) { d = dd; best = coarse[i]; } }
    if (d > 0.12) { d = 1; for (i = 0; i < FRACS.length; i++) { dd = Math.abs(FRACS[i][0] - rest); if (dd < d) { d = dd; best = FRACS[i]; } } }
    if (best[0] === 1) { whole += 1; best = FRACS[0]; }
    if (!whole && !best[1]) return rest > 0.06 ? '1/8' : '';
    return (whole ? whole : '') + (whole && best[1] ? ' ' : '') + best[1];
  };

  /** Grams (or ml) a recipe's ingredient list adds up to, skipping frying oil and the like. */
  C.yieldGrams = function (lines) {
    var g = 0;
    (lines || []).forEach(function (l) {
      if (!l || l.text) return;
      if (/for (deep[- ])?frying|frying oil|for dusting|for greasing|for the pan|for the top|for brushing/i.test(l.name || '')) return;
      var m = C.num(l.amount_metric);
      if (isFinite(m) && (l.unit_metric === 'g' || l.unit_metric === 'ml')) g += m;
    });
    return g;
  };

  /** Scale factor for a slot: the template's need in grams over the batch, held to 0.5x-2x, in quarter steps. */
  C.factor = function (slot, detail) {
    if (!slot || !slot.need_g) return 1;
    var g = C.yieldGrams(detail && detail.ingredients);
    if (!g) return 1;
    var f = slot.need_g / g;
    f = Math.max(0.5, Math.min(2, f));
    return Math.round(f * 4) / 4;
  };

  /** A scaled US amount in the unit a cook would use: 1/8 cup -> 2 tbsp, 1/2 tbsp -> 1 1/2 tsp, 3 cups -> "cups". */
  C.usUnit = function (n, unit) {
    var u = String(unit || '').toLowerCase().replace(/s$/, '');
    if (u === 'cup' && n > 0 && n < 0.25) { n *= 16; u = 'tbsp'; }
    if (u === 'tbsp' && n > 0 && n < 1) { n *= 3; u = 'tsp'; }
    if (u === 'cup' && n > 1) u = 'cups';
    return { n: n, unit: u };
  };

  /** One ingredient line scaled: keeps the name, rewrites the amounts (and the unit when a smaller one reads better). */
  C.scaleLine = function (l, f) {
    if (!l || l.text) return l;
    var a = C.num(l.amount), m = C.num(l.amount_metric);
    var us = isFinite(a) ? C.usUnit(a * f, l.unit) : { n: NaN, unit: l.unit || '' };
    return { name: l.name, unit: isFinite(a) ? us.unit : (l.unit || ''), heading: l.heading || '',
      amount: isFinite(a) ? C.fmt(us.n, us.unit) : (l.amount || ''),
      amount_metric: isFinite(m) ? C.fmt(m * f, l.unit_metric) : (l.amount_metric || ''), unit_metric: l.unit_metric || '',
      n: isFinite(a) ? a * f : NaN, g: isFinite(m) ? m * f : NaN };
  };

  /* ------------------------------------------------------------------ shopping list --- */

  /** "cold unsalted butter, cut in cubes" -> "unsalted butter"; "large eggs" -> "large eggs". */
  C.key = function (name) {
    var n = String(name || '').toLowerCase().split(/,|\(/)[0];
    n = n.replace(/\b(cold|softened|melted|room[- ]temperature|warm|hot|chilled|sifted|packed|finely|coarsely|chopped|cubed|divided|plus more|as needed|fresh|large|about)\b/g, ' ');
    n = n.replace(/\s+/g, ' ').trim();
    if (/^eggs?$/.test(n) || /^egg$/.test(n)) n = 'eggs';
    return n;
  };

  /** The ingredient's name as it reads on a list: the key's words, in the recipe's own capitalization. */
  C.display = function (name) {
    var n = String(name || '').split(/,|\(/)[0];
    n = n.replace(/\b(cold|softened|melted|room[- ]temperature|warm|hot|chilled|sifted|packed|finely|coarsely|chopped|cubed|divided|plus more|as needed|fresh|about)\b/gi, ' ');
    n = n.replace(/\s+/g, ' ').trim();
    return n || name;
  };

  /** Merge every part's lines by ingredient and unit: one line per ingredient with the total. */
  C.shopping = function (parts) {
    var rows = {}, order = [];
    parts.forEach(function (p) {
      (p.ingredients || []).forEach(function (l) {
        if (!l || l.text || !l.name) return;
        var unit = (l.unit || '').replace(/s$/, ''), key = C.key(l.name), k = key + '|' + unit;
        if (!rows[k]) { rows[k] = { name: key === 'eggs' ? 'eggs' : C.display(l.name), key: key, unit: unit, n: 0, g: 0, gu: l.unit_metric || '', from: [], any: false }; order.push(k); }
        var r = rows[k];
        if (isFinite(l.n)) r.n += l.n; else r.any = true;
        if (isFinite(l.g)) r.g += l.g;
        if (r.from.indexOf(p.title) === -1) r.from.push(p.title);
      });
    });
    return order.map(function (k) {
      var r = rows[k];
      var amt = r.n ? C.fmt(r.n, r.unit) : '';
      var metric = r.g ? C.fmt(r.g, r.gu) + ' ' + r.gu : '';
      var name = r.key === 'eggs' && r.n === 1 ? 'large egg' : (r.key === 'eggs' ? 'large eggs' : r.name);
      return { name: name, amount: amt, unit: r.n ? r.unit + (r.n > 1 && r.unit === 'cup' ? 's' : '') : '', metric: metric, from: r.from, any: r.any };
    });
  };

  /* ------------------------------------------------------------------ title and method --- */

  var BASE_STRIP = /\b(cake layers|layers|cupcakes|cupcake batter|batter|base|dough for filling|dough|pie crust|crust|tart shell|shell|cheesecake base|for doughnuts|doughnuts|doughnut)\b/gi;

  C.short = function (comp) {
    if (!comp) return '';
    var s = comp.t.replace(/\(.*?\)/g, '').replace(BASE_STRIP, ' ').replace(/\s+/g, ' ').trim();
    return s || comp.t;
  };

  C.flavorWord = function (comp) {
    if (!comp) return '';
    if (comp.f && !/^(classic|other)$/i.test(comp.f)) return comp.f;
    return C.short(comp).split(' ')[0] || '';
  };

  function titleCase(s) { return s.replace(/\s+/g, ' ').trim().replace(/\b([a-z])/g, function (m) { return m.toUpperCase(); }); }

  C.title = function (idx, type, state) {
    var get = function (k) { return state[k] ? idx.byHandle[state[k]] : null; };
    var vals = {
      base_short: C.short(get('base')), filling_flavor: C.flavorWord(get('filling')), topping_flavor: C.flavorWord(get('topping')) || C.flavorWord(get('sauce')),
      filling_short: get('filling') ? C.short(get('filling')).replace(/\b(pie filling|filling)\b/gi, '').trim() : ''
    };
    var t = type.title.replace(/\{(\w+)\}/g, function (m, k) { return vals[k] || ''; });
    t = t.replace(/\s+/g, ' ').trim();
    // "Vanilla Vanilla Butter Layer Cake" -> "Vanilla Butter Layer Cake": a word appears once
    var seen = {}, words = [];
    t.split(' ').forEach(function (w) { var k = w.toLowerCase(); if (!seen[k]) { seen[k] = true; words.push(w); } });
    return titleCase(words.join(' '));
  };

  function stripNumber(s) { return String(s || '').replace(/^\s*\d+[.)]\s*/, '').trim(); }

  /** The full method: each part's own steps under its name, then the template's assembly with names filled in. */
  C.method = function (idx, type, state, parts) {
    var names = {};
    parts.forEach(function (p) { names[p.slot] = p.title; });
    var sections = parts.map(function (p) {
      return { slot: p.slot, title: p.title, url: p.url, steps: (p.steps || []).map(stripNumber).filter(Boolean) };
    });
    var assembly = [];
    (type.assembly || []).forEach(function (s) {
      var skip = false;
      var line = s.replace(/\{(\w+)\}/g, function (m, k) { if (!names[k]) skip = true; return 'the ' + (names[k] || '').replace(/^the /i, ''); });
      if (!skip) assembly.push(line.replace(/\bthe the\b/g, 'the'));
    });
    return { sections: sections, assembly: assembly };
  };

  /** Times, diet labels, allergens, vanilla forms, equipment across the parts. */
  C.labels = function (parts) {
    var prep = 0, cook = 0, wait = 0, allergens = {}, diets = null, forms = {}, equip = {}, review = false, storage = [];
    parts.forEach(function (p) {
      var d = p.detail || {};
      prep += +d.prep || 0; cook += +d.cook || 0;
      wait = Math.max(wait, (+d.chill || 0) + (+d.proof || 0) + (+d.rest || 0));
      (d.allergens || []).forEach(function (a) { allergens[a] = true; });
      if (d.allergen_review) review = true;
      var dd = (d.diet || []).map(function (x) { return String(x).toLowerCase().replace(/[ _]/g, '-'); });
      diets = diets === null ? dd : diets.filter(function (x) { return dd.indexOf(x) !== -1; });
      if (d.vanilla_form) forms[String(d.vanilla_form).toLowerCase()] = true;
      (p.comp && p.comp.e || []).forEach(function (e) { equip[e] = true; });
      if (d.storage) storage.push({ title: p.title, text: d.storage });
    });
    return { prep: prep, cook: cook, wait: wait, total: prep + cook + wait, allergens: Object.keys(allergens).sort(), review: review,
      diets: diets || [], forms: Object.keys(forms), equipment: Object.keys(equip).sort(), storage: storage };
  };

  // a pie filling written as a whole pie lists its crust; the crust is its own part here
  var CRUST_LINE = /\b(pastry for|pie crust|pie shell|pie dough|graham cracker crust|cookie crust|crust\b)/i;
  var CRUST_STEP = /^(roll out|roll the (dough|pastry)|fit (it|the (dough|pastry)) into)/i;

  /** Everything the result panel shows. details = {handle: detail json}, servings = people to feed. */
  C.compose = function (idx, type, state, details, servings) {
    var people = +servings || type.servings;
    var mult = people / type.servings;
    var parts = [];
    type.slots.forEach(function (s) {
      var h = state[s.key];
      if (!h) return;
      var comp = idx.byHandle[h], d = details[h];
      if (!comp || !d) return;
      var f = C.factor(s, d) * mult;
      var lines = d.ingredients || [], steps = d.steps || [];
      if (type.key === 'pie' && s.key !== 'base' && state.base) {
        lines = lines.filter(function (l) { return !(l && l.name && CRUST_LINE.test(l.name)); });
        steps = steps.filter(function (st) { return !CRUST_STEP.test(String(st).replace(/^\s*\d+[.)]\s*/, '')); });
      }
      // the strip shows the part on this dessert (the card photo picked in the steps), not the recipe's own photo
      var image = (comp.ii && comp.ii[type.key]) || comp.i || d.image;
      parts.push({ slot: s.key, label: s.label, handle: h, comp: comp, detail: d, title: comp.t, url: d.url || ('/blogs/recipes/' + h),
        image: image, factor: Math.round(f * 100) / 100, ingredients: lines.map(function (l) { return C.scaleLine(l, f); }), steps: steps });
    });
    return { title: C.title(idx, type, state), type: type, people: people, parts: parts, shopping: C.shopping(parts),
      method: C.method(idx, type, state, parts), labels: C.labels(parts) };
  };

  /* ------------------------------------------------------------------ url state --- */

  var KEYS = { base: 'b', filling: 'f', frosting: 'fr', topping: 'to', crunch: 'cc', sauce: 's' };

  C.toHash = function (state) {
    var q = [];
    if (state.type) q.push('t=' + state.type);
    Object.keys(KEYS).forEach(function (k) { if (state[k]) q.push(KEYS[k] + '=' + encodeURIComponent(state[k])); });
    if (state.servings) q.push('n=' + state.servings);
    return q.length ? '#' + q.join('&') : '';
  };

  C.fromHash = function (hash) {
    var st = {}, map = { t: 'type', b: 'base', f: 'filling', fr: 'frosting', to: 'topping', cc: 'crunch', s: 'sauce', n: 'servings' };
    String(hash || '').replace(/^#/, '').split('&').forEach(function (kv) {
      var i = kv.indexOf('='); if (i < 1) return;
      var k = map[kv.slice(0, i)]; if (!k) return;
      st[k] = decodeURIComponent(kv.slice(i + 1));
    });
    if (st.servings) st.servings = parseInt(st.servings, 10) || undefined;
    return st;
  };
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));
